import type { Question } from '../../config/types';
import { formatCurrencyValue, formatTotalValue } from '../form-utils';
import {
  getListAnswerEntries,
  getListAnswerKeys,
  getListQuestionDescriptors,
  isListQuestionActive,
  type ListQuestionDescriptor,
} from '../list-question-utils';
import {
  getListPageExcelColumnDependency,
  getListPageExcelColumns,
  getListPageExcelColumnTitle,
  type ListPageExcelColumn,
} from './columns';
import type { ListPageExcelValidatedRow } from './validation';
import { validateListPageExcelRows } from './validation';

export type ListPageExcelOptions = {
  title: string;
  pageName: string;
  questions: Question[];
  answers: Record<string, unknown>;
  tenant: string;
};

export type ListPageExcelSchema = {
  descriptors: ListQuestionDescriptor[];
  columns: ListPageExcelColumn[];
};

export const assertListPageExcelSupported = (descriptors: ListQuestionDescriptor[]) => {
  const unsupported = descriptors.filter(
    (descriptor) =>
      descriptor.kind === 'unsupported' ||
      ((descriptor.kind === 'list' || descriptor.kind === 'multi-list') &&
        descriptor.options.length === 0),
  );
  if (unsupported.length === 0) return;
  const fields = unsupported
    .map(({ question }) => `${question.title} (${question.questionType})`)
    .join(', ');
  throw new Error(`Excel import is not available for unsupported fields: ${fields}.`);
};

export const getListPageExcelSchema = (questions: Question[]): ListPageExcelSchema => {
  const descriptors = getListQuestionDescriptors(questions);
  assertListPageExcelSupported(descriptors);
  return { descriptors, columns: getListPageExcelColumns(descriptors) };
};

export const getListPageEditorColumnWidth = (column: ListPageExcelColumn) => {
  const titleLength = getListPageExcelColumnTitle(column).length;
  if (column.descriptors.every(({ kind }) => kind === 'number')) {
    return Math.max(10, Math.min(16, Math.ceil(titleLength / 3) + 2));
  }

  const longestOptionLength = Math.max(
    0,
    ...column.descriptors
      .filter(({ kind }) => kind === 'list')
      .flatMap(({ options }) => options.map(({ label }) => label.length)),
  );
  if (longestOptionLength > 0) {
    const threeLineTitleWidth = Math.ceil(titleLength / 3) + 2;
    return Math.max(12, threeLineTitleWidth, longestOptionLength + 4);
  }

  return Math.max(18, Math.min(32, Math.ceil(titleLength / 2) + 4));
};

export const getListPageEditorColumnTotal = (
  column: ListPageExcelColumn,
  rows: unknown[][],
  columnIndex: number,
): string | undefined => {
  if (!column.descriptors.every(({ kind }) => kind === 'number')) return undefined;
  const total = rows.reduce((sum, row) => {
    const value = Number(
      String(row[columnIndex] ?? '')
        .trim()
        .replace(/[\u00a3,\s]/g, ''),
    );
    return Number.isFinite(value) ? sum + value : sum;
  }, 0);
  const question = column.descriptors[0].question;
  const decimalMatch = String(question.rules?.decimal ?? '')
    .trim()
    .match(/^decimal\((\d+)\)$/i);
  const decimalPlaces = decimalMatch ? Number.parseInt(decimalMatch[1], 10) : undefined;
  if (decimalPlaces !== undefined) {
    const formatted = total.toLocaleString('en-GB', {
      minimumFractionDigits: decimalPlaces,
      maximumFractionDigits: decimalPlaces,
    });
    return question.questionType === 'costInput' ? formatCurrencyValue(formatted) : formatted;
  }
  return question.questionType === 'costInput'
    ? formatTotalValue(total, question)
    : total.toLocaleString('en-GB', { maximumFractionDigits: 20 });
};

export const isListPageEditorColumnRequired = (column: ListPageExcelColumn) =>
  column.descriptors.some(({ question }) => question.rules?.required);

export const getListPageEditorColumnHint = (column: ListPageExcelColumn) => {
  const descriptor = column.descriptors[0];
  if (descriptor.kind === 'list' || descriptor.kind === 'multi-list') return undefined;
  const { question } = descriptor;
  if (question.emailList) return 'type: email list';
  if (question.rules?.email) return 'type: email';
  if (question.rules?.telephone) return 'type: telephone number';
  if (descriptor.kind === 'number') {
    return question.rules?.decimal || question.rules?.float ? 'type: decimal' : 'type: number';
  }
  if (descriptor.kind === 'text') return 'type: freetext';
  return `type: ${question.questionType}`;
};

export const getListPageExcelRequirement = (column: ListPageExcelColumn) => {
  const descriptor = column.descriptors[0];
  const dependency = getListPageExcelColumnDependency(column);
  const requirement = column.descriptors.some(({ question }) => question.rules?.required)
    ? '*required'
    : 'optional';
  const type =
    descriptor.kind === 'multi-list'
      ? 'multiple list'
      : descriptor.kind === 'unsupported'
        ? descriptor.question.questionType
        : descriptor.kind;
  const optionText = dependency
    ? ` [options change with "${dependency.questionTitle}"]`
    : descriptor.options.length > 0
      ? ` [${descriptor.options.map(({ label }) => label).join('; ')}]`
      : '';
  const conditions = dependency ? descriptor.conditions.slice(0, -1) : descriptor.conditions;
  const conditionText = conditions
    .map(({ questionTitle, optionLabel }) => ` when "${questionTitle}" is "${optionLabel}"`)
    .join(' and');
  return `${requirement}: ${type}${optionText}${conditionText}`;
};

export const formatListPageExcelValue = (descriptor: ListQuestionDescriptor, value: unknown) => {
  if (value === null || value === undefined) return '';
  if (descriptor.kind === 'list') {
    const storedValue = String(value);
    return descriptor.options.find((option) => option.value === storedValue)?.label || storedValue;
  }
  if (descriptor.kind === 'multi-list') {
    const values = Array.isArray(value)
      ? value.map(String)
      : String(value)
          .split(/[;\r\n]+/)
          .map((item) => item.trim())
          .filter(Boolean);
    return values
      .map(
        (storedValue) =>
          descriptor.options.find((option) => option.value === storedValue)?.label || storedValue,
      )
      .join('; ');
  }
  return typeof value === 'string' || typeof value === 'number' ? value : String(value);
};

export const getListPageEditorRows = (
  options: ListPageExcelOptions,
  schema = getListPageExcelSchema(options.questions),
): unknown[][] =>
  getListAnswerEntries(options.answers, options.questions).map((entry) =>
    schema.columns.map((column) => {
      const activeDescriptor = column.descriptors.find((candidate) =>
        isListQuestionActive(candidate, entry.answers),
      );
      return activeDescriptor
        ? formatListPageExcelValue(activeDescriptor, entry.answers[activeDescriptor.question.name])
        : '';
    }),
  );

export const buildListPageReplacementAnswers = (
  options: ListPageExcelOptions,
  rows: ListPageExcelValidatedRow[],
) => {
  const replacementAnswers: Record<string, unknown> = {};
  getListAnswerKeys(options.answers, options.questions).forEach((answerName) => {
    replacementAnswers[answerName] = Array.isArray(options.answers[answerName]) ? [] : '';
  });

  rows.forEach(({ answers }, rowIndex) => {
    Object.entries(answers).forEach(([fieldName, value]) => {
      replacementAnswers[`${rowIndex + 1}-${fieldName}`] = value;
    });
  });
  return replacementAnswers;
};

export const validateListPageEditorRows = (
  options: ListPageExcelOptions,
  rows: unknown[][],
  schema = getListPageExcelSchema(options.questions),
) =>
  validateListPageExcelRows({
    rows: rows
      .map((values, rowIndex) => ({ worksheetRow: rowIndex + 1, values }))
      .filter(({ values }) => values.some((value) => String(value ?? '').trim() !== '')),
    descriptors: schema.descriptors,
    tenant: options.tenant,
  });
